import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Brain, CheckCircle2, Cloud, Lock, Plug, Shield, Sparkles, Video, Wand2, X } from "lucide-react";
import { MarketingShell } from "@/components/MarketingShell";

// Brand word styling — currently keeps Probe inline with surrounding monochrome text.
const PROBE_ACCENT = "";

const ProbeWord = () => <span>Probe</span>;

// Split a string on the literal word "Probe" and render each occurrence with the accent color.
const renderWithBrand = (text) =>
  text.split(/(Probe)/g).map((part, i) =>
    part === "Probe" ? <ProbeWord key={i} /> : part
  );

const PROMPT_DEMO_LINES = [
  { p: "$", t: "probe describe 'test login and add Pro plan to cart'", prompt: true },
  { p: ">", t: "✦ Agent draft", muted: true },
  { p: "  1", t: 'goto "https://acme.app"' },
  { p: "  2", t: 'click [data-testid="open-login"]' },
  { p: "  3", t: "fill #email     ← saved login state" },
  { p: "  4", t: "fill #password  ← saved login state" },
  { p: "  5", t: 'click "Sign in"' },
  { p: "  6", t: 'expect "Welcome back"' },
  { p: "  7", t: 'click "Pricing › Pro"' },
  { p: "  8", t: 'click "Add to cart"' },
  { p: "  9", t: "expect cart badge == 1" },
  { p: "✓", t: "Test generated", ok: true },
  { p: "✓", t: "Validation passed · 11.4s · 9 steps", ok: true },
];

const RECORDING_DEMO_LINES = [
  { p: "●", t: "Recording in progress...", prompt: true },
  { p: "✓", t: "Login clicked", ok: true },
  { p: "✓", t: "Credentials entered", ok: true },
  { p: "✓", t: "Pro plan selected", ok: true },
  { p: "✓", t: "Added to cart", ok: true },
  { p: ">", t: "✦ Recording complete", muted: true },
  { p: "✓", t: "9 actions captured", ok: true },
  { p: "✓", t: "Test generated", ok: true },
  { p: "✓", t: "Validation passed", ok: true },
];

const DEMO_MODES = [
  { id: "prompt", label: "Prompt", lines: PROMPT_DEMO_LINES },
  { id: "recording", label: "Recording", lines: RECORDING_DEMO_LINES },
];

const FEATURES = [
  { icon: Sparkles, title: "AI test generation", body: "Generate tests from plain English." },
  { icon: Shield, title: "Self-healing selectors", body: "Adapts to UI changes automatically." },
  { icon: Video, title: "Record & playback", body: "Capture user journeys as tests." },
  { icon: Cloud, title: "Cloud browsers", body: "Run tests without managing infrastructure." },
  { icon: Lock, title: "Login state vault", body: "Reuse authenticated sessions across runs." },
];

const HOW_STEPS = [
  { n: "01", title: "Connect", body: "Add your application URL.", icon: Plug },
  { n: "02", title: "Describe", body: "Type a prompt or record a flow.", icon: Brain },
  { n: "03", title: "Generate", body: "Probe writes and validates the test.", icon: Wand2 },
  { n: "04", title: "Run & Heal", body: "Tests run and adapt to UI changes.", icon: Sparkles },
];

const COMPARISON_ROWS = [
  { traditional: "Write scripts manually", probe: "Describe in English" },
  { traditional: "Maintain selectors", probe: "Self-healing by AI" },
  { traditional: "Build test suites", probe: "Generate instantly" },
  { traditional: "Investigate failures manually", probe: "AI identifies failures" },
  { traditional: "Weeks to automate", probe: "Minutes to automate" },
];

const PRICING_TIERS = [
  {
    name: "Free",
    price: "$0",
    period: "/forever",
    blurb: "Evaluate Probe on a single project.",
    usage: "50 test execution minutes / month",
    usageNote: "Perfect for evaluating Probe.",
    features: [
      "25 AI-generated tests",
      "Plain English test creation",
      "User journey recording",
    ],
    cta: "Start free",
    href: "/signup",
    highlight: false,
  },
  {
    name: "Pro",
    price: "$99",
    period: "/month",
    blurb: "For teams running regression on every release.",
    usage: "5,000 test execution minutes / month",
    usageNote: "≈ 83 hours of browser testing per month.",
    features: [
      "Unlimited users",
      "Unlimited tests",
      "Self-healing tests",
      "Scheduled runs",
      "Encrypted login vault",
    ],
    cta: "Start Pro",
    href: "/signup",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom pricing",
    period: "",
    blurb: "Security, scale and enterprise support.",
    usage: "Custom execution minute allocation",
    features: [
      "SSO and role-based access",
      "Advanced audit trails",
      "Dedicated onboarding and support",
      "SLA options",
    ],
    cta: "Schedule a demo",
    href: "mailto:support@helloprobe.com",
    highlight: false,
  },
];

export default function MarketingHomePage() {
  const location = useLocation();
  const [demoMode, setDemoMode] = useState("prompt");
  const activeDemo = DEMO_MODES.find((m) => m.id === demoMode) ?? DEMO_MODES[0];

  // Smooth-scroll to the requested anchor whenever the hash changes — including
  // initial mount when arriving from /pricing or any other route via /#anchor.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    const id = location.hash.replace("#", "");
    // Wait a tick for the section to mount before scrolling.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => clearTimeout(t);
  }, [location.hash, location.key]);

  return (
    <MarketingShell>
      {/* ===================== HERO ===================== */}
      <section className="relative bg-[#0B0B0C] text-white overflow-hidden">
        {/* Gradient glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(circle, #3B82F6 0%, rgba(59,130,246,0) 60%)" }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse at center, black 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-32 lg:py-44 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="max-w-[700px]">
            <h1
              data-testid="hero-headline"
              className="font-['Geist'] font-semibold text-[34px] sm:text-5xl lg:text-[54px] leading-[1.08] tracking-tight text-white"
            >
              Test your web application
              <br />
              using AI agents.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/65 leading-[1.65] max-w-xl">
              Write tests in plain English or record a user journey. Probe generates, executes and maintains your tests automatically.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/signup"
                data-testid="hero-cta-get-started"
                className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white h-11 px-5 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors shadow-[0_8px_24px_-12px_rgba(59,130,246,0.6)]"
              >
                Start testing free <ArrowRight size={15} />
              </Link>
              <Link
                to="/#pricing"
                data-testid="hero-cta-pricing"
                className="inline-flex items-center justify-center h-11 px-5 rounded-md text-sm font-medium text-white/85 border border-white/15 hover:border-white/30 hover:bg-white/[0.04] transition-colors"
              >
                See pricing
              </Link>
            </div>
            <p
              data-testid="hero-audience"
              className="mt-7 font-mono text-[11px] tracking-[0.18em] uppercase text-white/40"
            >
              Built for engineering, QA and product teams shipping modern web applications.
            </p>
            <div
              data-testid="hero-trust-signal"
              className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.04em] text-white/45"
            >
              <span className="inline-block w-1 h-1 rounded-full bg-[#3B82F6]" />
              From the team behind <span className="text-white/70 font-medium">FastCollab</span>
            </div>
          </div>

          {/* Terminal mock with toggle */}
          <div data-testid="hero-terminal-mock">
            <div
              data-testid="demo-mode-toggle"
              role="tablist"
              className="inline-flex items-center gap-1 border border-white/10 bg-white/[0.04] backdrop-blur rounded-md p-1 mb-4"
            >
              {DEMO_MODES.map((m) => {
                const isActive = m.id === demoMode;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setDemoMode(m.id)}
                    data-testid={`demo-mode-${m.id}`}
                    className={`px-3.5 py-1.5 rounded text-xs font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] ${
                      isActive
                        ? "bg-[#FFFFFF] !text-[#0B0B0C] font-semibold shadow-sm"
                        : "text-white/55 hover:text-white"
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute -inset-px rounded-xl"
                style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.45), rgba(59,130,246,0) 60%)" }}
              />
              <div className="relative bg-[#0F172A] border border-white/10 ring-1 ring-white/5 rounded-xl text-white font-mono text-[12px] leading-[1.6] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)]">
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10">
                  <span className="w-2.5 h-2.5 bg-white/20 rounded-full" />
                  <span className="w-2.5 h-2.5 bg-white/20 rounded-full" />
                  <span className="w-2.5 h-2.5 bg-white/20 rounded-full" />
                  <span className="ml-3 text-[10px] tracking-[0.2em] uppercase text-white/35">
                    probe · {activeDemo.id === "recording" ? "recording" : "live"}
                  </span>
                </div>
                <div className="p-5 space-y-1 min-h-[380px]" data-testid={`demo-output-${activeDemo.id}`}>
                  {activeDemo.lines.map((line, i) => (
                    <div key={i} className="flex gap-3">
                      <span className={`shrink-0 w-4 text-right ${line.ok || line.prompt ? "text-white" : "text-white/25"}`}>
                        {line.p}
                      </span>
                      <span className={
                        line.ok ? "text-[#3B82F6] font-medium"
                        : line.prompt ? "text-white"
                        : line.muted ? "text-white/35"
                        : "text-white/55"
                      }>
                        {line.t}
                      </span>
                    </div>
                  ))}
                  <div className="flex gap-3 pt-1">
                    <span className="text-white/25 w-4 text-right">$</span>
                    <span className="text-white inline-block w-2 h-3 bg-white animate-pulse" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FEATURES ===================== */}
      <section id="features" className="bg-[#111827] text-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-36 lg:py-48">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#3B82F6]">Features</p>
          <h2 className="font-['Geist'] font-semibold text-3xl sm:text-[40px] tracking-tight mt-3 max-w-2xl text-white leading-[1.15]">
            Built for modern web application testing.
          </h2>
          <p className="mt-4 text-white/60 max-w-xl leading-relaxed">
            Everything needed to automate browser testing. Built for teams shipping every day.
          </p>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="flex flex-col rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur p-6 hover:border-white/15 hover:bg-white/[0.06] transition-colors"
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/[0.06] border border-white/10 text-[#3B82F6] mb-5">
                  <f.icon size={16} strokeWidth={1.75} />
                </div>
                <h3 className="font-['Geist'] font-semibold text-base tracking-tight text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section id="how" className="bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-36 lg:py-48">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#3B82F6]">How it works</p>
          <h2 className="font-['Geist'] font-semibold text-3xl sm:text-[40px] tracking-tight mt-3 text-[#0F172A] leading-[1.15]">
            Create automated tests in four steps.
          </h2>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_STEPS.map((s) => (
              <div
                key={s.n}
                className="rounded-xl border border-[#E5E7EB] bg-white p-7 hover:border-[#CBD5E1] hover:shadow-[0_8px_30px_-15px_rgba(15,23,42,0.15)] transition-all"
                data-testid={`how-step-${s.n}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-xs tracking-[0.18em] text-[#3B82F6]">{s.n}</span>
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#F1F5F9] text-[#0F172A]">
                    <s.icon size={16} strokeWidth={1.75} />
                  </div>
                </div>
                <h3 className="font-['Geist'] font-semibold text-lg tracking-tight text-[#0F172A]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{renderWithBrand(s.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHY PROBE ===================== */}
      <section id="why" className="bg-[#F8FAFC] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-36 lg:py-48">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#3B82F6]">Comparison</p>
          <h2 className="font-['Geist'] font-semibold text-3xl sm:text-[40px] tracking-tight mt-3 text-[#0F172A] leading-[1.15]">
            Why teams choose Probe.
          </h2>

          <div className="mt-16 grid md:grid-cols-2 gap-5 lg:gap-6" data-testid="why-probe-comparison">
            {/* Traditional */}
            <div
              data-testid="why-traditional-box"
              className="rounded-xl border border-[#E2E8F0] bg-white p-8"
            >
              <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#94A3B8]">
                Traditional Test Automation
              </p>
              <ul className="mt-7 space-y-5">
                {COMPARISON_ROWS.map((row, i) => (
                  <li
                    key={row.traditional}
                    data-testid={`why-traditional-item-${i}`}
                    className="flex items-start gap-3 text-[15px] text-[#6B7280]"
                  >
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#E2E8F0] text-[#CBD5E1] shrink-0">
                      <X size={12} strokeWidth={2.5} />
                    </span>
                    <span className="leading-relaxed">{row.traditional}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Probe */}
            <div
              data-testid="why-probe-box"
              className="rounded-xl border border-[#3B82F6]/30 bg-white p-8 shadow-[0_24px_60px_-30px_rgba(59,130,246,0.35)]"
            >
              <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#3B82F6]">
                Probe
              </p>
              <ul className="mt-7 space-y-5">
                {COMPARISON_ROWS.map((row, i) => (
                  <li
                    key={row.probe}
                    data-testid={`why-probe-item-${i}`}
                    className="flex items-start gap-3 text-[15px] text-[#0F172A]"
                  >
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#3B82F6]/10 text-[#3B82F6] shrink-0">
                      <CheckCircle2 size={14} strokeWidth={2} />
                    </span>
                    <span className="leading-relaxed">{row.probe}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PRICING ===================== */}
      <section id="pricing" className="bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-36 lg:py-48">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#3B82F6]">Pricing</p>
          <h2 className="font-['Geist'] font-semibold text-3xl sm:text-[40px] tracking-tight mt-3 text-[#0F172A] leading-[1.15]">
            Start free. Scale as you grow.
          </h2>
          <p className="mt-4 text-[15px] text-[#64748B] max-w-xl leading-relaxed">
            Only pay for the browser testing time you use. Execute tests in managed cloud browsers without maintaining infrastructure.
          </p>
          <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.name}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                className={`relative flex flex-col rounded-2xl bg-white transition-all ${
                  tier.highlight
                    ? "border-2 border-[#3B82F6] -translate-y-3 lg:scale-[1.04] p-10 lg:p-12 shadow-[0_30px_70px_-25px_rgba(59,130,246,0.45)] z-10"
                    : "border border-[#E5E7EB] p-9 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)] hover:shadow-[0_10px_30px_-15px_rgba(15,23,42,0.15)]"
                }`}
              >
                {tier.highlight && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-white border border-[#3B82F6] text-[#3B82F6] text-[11px] font-medium tracking-wide px-3 py-1 rounded-full shadow-[0_4px_12px_-2px_rgba(59,130,246,0.25)]">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
                    Most popular
                  </span>
                )}
                <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#64748B]">{tier.name}</p>
                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className={`font-['Geist'] font-semibold tracking-tight text-[#0F172A] ${tier.highlight ? "text-5xl" : "text-[42px]"}`}>{tier.price}</span>
                  {tier.period && (
                    <span className="text-sm text-[#94A3B8]">{tier.period}</span>
                  )}
                </div>
                <p className="mt-4 text-[15px] text-[#475569] leading-relaxed">{renderWithBrand(tier.blurb)}</p>
                {tier.usage && (
                  <div
                    data-testid={`pricing-usage-${tier.name.toLowerCase()}`}
                    className="mt-8 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-5"
                  >
                    <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#94A3B8]">
                      Included usage
                    </p>
                    <p className="mt-1.5 text-[15px] text-[#0F172A] font-medium">
                      {tier.usage}
                    </p>
                    {tier.usageNote && (
                      <p className="mt-1.5 text-[12px] text-[#94A3B8] leading-relaxed">
                        {tier.usageNote}
                      </p>
                    )}
                  </div>
                )}
                <ul className="mt-8 space-y-4 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[15px]">
                      <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#3B82F6]/10 text-[#3B82F6] shrink-0">
                        <CheckCircle2 size={13} strokeWidth={2.25} />
                      </span>
                      <span className="text-[#334155] leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
                {tier.href.startsWith("/") ? (
                  <Link
                    to={tier.href}
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                    className={`mt-10 inline-flex items-center justify-center h-12 px-5 rounded-md text-sm font-medium transition-colors ${
                      tier.highlight
                        ? "bg-[#3B82F6] text-white hover:bg-[#2563EB] shadow-[0_8px_24px_-12px_rgba(59,130,246,0.6)]"
                        : "bg-[#0F172A] text-white hover:bg-[#1E293B]"
                    }`}
                  >
                    {tier.cta}
                  </Link>
                ) : (
                  <a
                    href={tier.href}
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                    className="mt-10 inline-flex items-center justify-center h-12 px-5 rounded-md text-sm font-medium border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
                  >
                    {tier.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
          <p
            data-testid="pricing-usage-note"
            className="mt-12 text-center text-sm text-[#64748B]"
          >
            Additional test execution minutes can be purchased as usage grows.
          </p>
          <p
            data-testid="pricing-minute-definition"
            className="mt-2 text-center text-xs text-[#94A3B8]"
          >
            One execution minute equals one minute of automated browser testing.
          </p>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative bg-[#0B0B0C] text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #3B82F6 0%, rgba(59,130,246,0) 65%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 lg:py-32 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-3xl">
            <h2 className="font-['Geist'] font-semibold text-2xl sm:text-3xl lg:text-[34px] tracking-tight leading-[1.2] text-white">
              Stop maintaining test scripts. Let Probe generate, execute and maintain your tests.
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            <Link
              to="/signup"
              data-testid="final-cta-get-started"
              className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white h-11 px-5 rounded-md text-sm font-medium whitespace-nowrap hover:bg-[#2563EB] transition-colors shadow-[0_8px_24px_-12px_rgba(59,130,246,0.6)]"
            >
              Start testing free <ArrowRight size={15} />
            </Link>
            <Link
              to="/login"
              data-testid="final-cta-signin"
              className="inline-flex items-center justify-center h-11 px-5 rounded-md text-sm font-medium whitespace-nowrap text-white/85 border border-white/15 hover:border-white/30 hover:bg-white/[0.04] transition-colors"
            >
              I have an account
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
